<template>
  <!-- Dialogs must escape dropdowns, collapsed panels and mobile stacking contexts. -->
  <Teleport to="body">
    <div ref="modal" v-bind="$attrs" tabindex="-1" :aria-label="title" :class="`modal fade ${size ? `modal-${size}` : ''}`">
      <div :class="`modal-dialog ${scrollable ? 'modal-dialog-scrollable' : ''} ${fullscreen ? 'modal-fullscreen' : ''} ${size ? `modal-fullscreen-${size}-down` : ''}`">
        <div class="modal-content">
          <div class="modal-header">
            <div class="d-flex flex-column gap-1 flex-grow-1">
              <div class="modal-title fw-semibold">
                {{ title }}
              </div>
              <div v-if="subtitle" class="modal-subtitle">
                {{ subtitle }}
              </div>
            </div>
            <button type="button" class="btn-close btn-flat" data-bs-dismiss="modal"/>
          </div>
          <div :class="['modal-body', contentClass]">
            <slot />
          </div>
          <div v-if="!hideFooter" class="modal-footer d-flex flex-row align-items-center">
            <slot name="buttons"/>
            <div class="flex-grow-1">
              <div v-if="busy" class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <button v-if="!hideCancel" :disabled="busy" class="btn btn-flat me-2" data-bs-dismiss="modal" @click="$emit('cancel')">
              {{ cancelTitle }}
            </button>
            <button v-if="!hideOk" :disabled="busy || disableOk" :class="`btn btn-${okVariant}`" @click="ok">
              {{ okTitle }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { markRaw } from 'vue';
import { createModalController } from './modalCoordinator';

export default {
  inheritAttrs: false,
  emits: ['update:modelValue', 'ok', 'cancel'],
  props: {
    modelValue: Boolean,
    title: String,
    subtitle: String,
    size: String,
    fullscreen: Boolean,
    scrollable: Boolean,
    busy: Boolean,
    okTitle: {
      type: String,
      default: () => 'OK'
    },
    cancelTitle: {
      type: String,
      default: () => 'Cancel'
    },
    okVariant: {
      type: String,
      default: () => 'primary'
    },
    hideOk: Boolean,
    disableOk: Boolean,
    hideCancel: Boolean,
    hideFooter: Boolean,
    contentClass: String
  },
  data() {
    return {
      modalController: null
    };
  },
  mounted() {
    this.modalController = markRaw(createModalController(this.$refs.modal, () => {
      this.$emit('update:modelValue', false);
    }));

    this.modalController.setOpen(this.modelValue);
  },
  beforeUnmount() {
    this.modalController?.dispose();
  },
  watch: {
    modelValue(value) {
      this.modalController?.setOpen(value);
    }
  },
  methods: {
    /** Let the caller validate and complete the action before closing the dialog. */
    ok() {
      this.$emit('ok');
    }
  }
}
</script>

<style>
.modal-body .modal-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.modal-body .modal-section > h3 {
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 0;
}
.modal-body .modal-section:not(:first-child) {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--bs-border-color);
}
</style>
